import { useState } from "react";
import { Search, Plus, Minus } from "lucide-react";
import { APP_NAME } from "@/lib/branding";
import { cn } from "@/lib/utils";

const FAQS = [
  {
    q: "Comment fonctionne l'essai gratuit ?",
    a: `Vous avez 14 jours pour tester ${APP_NAME} sans carte bancaire, avec accès complet à toutes les fonctionnalités du plan Business. À la fin de l'essai, vous confirmez votre plan et votre carte est prélevée. Vous pouvez résilier à tout moment depuis votre portail client Stripe.`,
  },
  {
    q: "Êtes-vous conformes à la facturation électronique 2026 ?",
    a: "Oui. Nos factures sont émises au format Factur-X (PDF/A-3 + XML CII), certifiées conformes à la réforme française de la facturation électronique entrant en vigueur en 2026. Nous serons opérateurs PDP au moment de l'obligation.",
  },
  {
    q: "Comment se passe la migration depuis Pennylane, Sage ou Cegid ?",
    a: "Nous récupérons vos exports FEC, vos factures, vos clients et vos écritures en une seule opération assistée par notre équipe. La migration prend typiquement moins de 48h et est offerte pour tout plan Business ou supérieur.",
  },
  {
    q: "Où sont hébergées mes données ?",
    a: "Vos données sont hébergées en France chez Scaleway sur infrastructure SecNumCloud (qualification ANSSI). Sauvegardes chiffrées quotidiennes, RGPD compliant, ISO 27001 en cours.",
  },
  {
    q: "Quelles banques sont supportées ?",
    a: "Toutes les banques françaises et européennes via les agrégateurs DSP2 Bridge et Powens : Crédit Agricole, BNP Paribas, Société Générale, Crédit Mutuel, LCL, HSBC, Qonto, Revolut, N26, Wise, plus 200 autres. Connexion en 30 secondes.",
  },
  {
    q: "Puis-je annuler à tout moment ?",
    a: "Oui, sans engagement. Vous pouvez résilier depuis votre portail client Stripe en un clic. Vos données restent exportables (FEC, CSV, PDF) pendant 90 jours après la résiliation.",
  },
  {
    q: "Y a-t-il une API pour se connecter à mes outils ?",
    a: `Oui, ${APP_NAME} expose une API REST complète et des webhooks. Nous proposons des connecteurs natifs vers Cegid, Sage, ACD, Isacompta, HubSpot, Slack, et une intégration Zapier pour tout le reste.`,
  },
  {
    q: "Le support est-il inclus ?",
    a: "Support par email inclus dans tous les plans. Support par chat sous 4h ouvrées à partir de Business. Customer Success Manager dédié pour Enterprise et Cabinet.",
  },
];

export default function FAQSection() {
  const [query, setQuery] = useState("");
  const [openIdx, setOpenIdx] = useState(0);

  const filtered = FAQS.filter(
    (f) =>
      !query ||
      f.q.toLowerCase().includes(query.toLowerCase()) ||
      f.a.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <section id="faq" className="py-20 lg:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#4F46E5]">
            Questions fréquentes
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B0F17]">
            On répond à ce que vous avez en tête.
          </h2>
        </div>

        {/* Search */}
        <div className="mt-8 relative max-w-xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher dans la FAQ…"
            data-testid="faq-search-input"
            className="w-full h-12 pl-11 pr-4 rounded-full border border-slate-200 bg-white text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
          />
        </div>

        {/* Accordion */}
        <div className="mt-10 space-y-2" data-testid="faq-accordion">
          {filtered.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-6">
              Aucune question ne correspond à cette recherche.
            </div>
          )}
          {filtered.map((f, i) => {
            const on = openIdx === i;
            return (
              <div
                key={f.q}
                className={cn(
                  "rounded-xl border transition-colors",
                  on ? "border-slate-300 bg-slate-50/60" : "border-slate-200 bg-white",
                )}
                data-testid={`faq-accordion-item-${i + 1}`}
              >
                <button
                  onClick={() => setOpenIdx(on ? -1 : i)}
                  className="w-full flex items-center justify-between text-left px-5 py-4"
                  aria-expanded={on}
                >
                  <span className="font-semibold text-sm sm:text-base text-[#0B0F17] pr-6">
                    {f.q}
                  </span>
                  <span
                    className={cn(
                      "shrink-0 h-7 w-7 rounded-full grid place-items-center transition-all",
                      on ? "bg-[#0B0F17] text-white rotate-180" : "bg-slate-100 text-slate-600",
                    )}
                  >
                    {on ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  </span>
                </button>
                {on && (
                  <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed animate-in fade-in duration-200">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
